import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI } from '../utils/api';
import DropboxIntegration from '../components/DropboxIntegration';

const DocumentUploadModal = ({ caseId, caseInfo, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setStep(2);
  };

  const handleUploadAndAnalyze = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setAnalyzing(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('case_id', caseId);
    formData.append('document_type', 'merchant_processing_contract');
    formData.append('analyze_contract', 'true');

    try {
      // Upload and analyze
      const response = await documentsAPI.uploadWithAnalysis(formData);
      
      setAnalysisResult(response.data.analysis);
      setAnalyzing(false);
      setStep(3);
      toast.success('Contract analyzed successfully!');
    } catch (error) {
      setAnalyzing(false);
      setUploading(false);
      toast.error(error.response?.data?.detail || 'Failed to upload and analyze document');
    }
  };

  const handleFinalSubmit = async () => {
    try {
      // Final submission to admin review
      await documentsAPI.submitForAdminReview(caseId, selectedFile.name);
      toast.success('Document submitted to admin for final review!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to submit for admin review');
    }
  };

  const getRecoveryLikelihoodColor = (likelihood) => {
    switch (likelihood?.toLowerCase()) {
      case 'high': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-amber-600 bg-amber-100';
      case 'low': return 'text-red-600 bg-red-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Upload Merchant Processing Contract</h2>
            <p className="text-sm text-slate-600 mt-1">AI-powered contract analysis for reserve recovery</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            disabled={uploading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex justify-center gap-2 p-4 bg-slate-50 border-b border-slate-200">
          <div className={`flex items-center gap-2`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-purple-600 text-white' : 'bg-slate-300'}`}>
              {step > 1 ? <CheckCircle size={16} /> : '1'}
            </div>
            <span className={`text-sm ${step >= 1 ? 'text-purple-600 font-semibold' : 'text-slate-500'}`}>Instructions</span>
          </div>
          <div className="w-16 h-0.5 bg-slate-300 self-center" />
          <div className={`flex items-center gap-2`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-purple-600 text-white' : 'bg-slate-300'}`}>
              {step > 2 ? <CheckCircle size={16} /> : '2'}
            </div>
            <span className={`text-sm ${step >= 2 ? 'text-purple-600 font-semibold' : 'text-slate-500'}`}>AI Analysis</span>
          </div>
          <div className="w-16 h-0.5 bg-slate-300 self-center" />
          <div className={`flex items-center gap-2`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-purple-600 text-white' : 'bg-slate-300'}`}>
              3
            </div>
            <span className={`text-sm ${step >= 3 ? 'text-purple-600 font-semibold' : 'text-slate-500'}`}>Results</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Instructions */}
          {step === 1 && (
            <div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                  <AlertCircle size={20} />
                  What to Upload
                </h3>
                <p className="text-purple-800 mb-4">
                  <strong>Upload your merchant processing contract</strong> - the agreement between you and the payment processor (e.g., Stripe, PayPal, Square).
                </p>
                <p className="text-sm text-purple-700">
                  This document typically contains reserve clauses, hold periods, and terms for fund release.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-slate-900 mb-4">If Your Reserves Are Being Withheld:</h3>
                <ol className="list-decimal pl-5 space-y-3 text-slate-700">
                  <li>
                    <strong>Review your contract</strong> for specific reserve clauses and timelines.
                  </li>
                  <li>
                    <strong>Request written justification</strong> from the payment processor.
                  </li>
                  <li>
                    <strong>Document all communications</strong> and payment statements.
                  </li>
                  <li>
                    <strong>Avoid abrupt account closure</strong>, which may prolong the hold.
                  </li>
                  <li>
                    <strong>Consult reserverelease.AI</strong> to determine the best course of action.
                  </li>
                </ol>
                <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded">
                  <p className="text-sm text-cyan-900">
                    <strong>Our AI will analyze your merchant agreement</strong> and advise whether a payment reserve dispute or legal claim is warranted.
                  </p>
                </div>
              </div>

              <DropboxIntegration caseId={caseId} documentType="merchant_processing_contract" />

              <div className="my-6 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                  <div className="flex-1 h-px bg-slate-300"></div>
                  <span>OR</span>
                  <div className="flex-1 h-px bg-slate-300"></div>
                </div>
              </div>

              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-purple-400 transition-colors">
                <Upload className="mx-auto mb-4 text-slate-400" size={48} />
                <label className="cursor-pointer">
                  <span className="text-purple-600 hover:text-purple-700 font-semibold text-lg">
                    Choose Contract Document
                  </span>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                </label>
                <p className="text-sm text-slate-500 mt-2">
                  PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Analysis Progress */}
          {step === 2 && (
            <div>
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="text-purple-600" size={40} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Selected Document</h3>
                <p className="text-slate-600">{selectedFile?.name}</p>
                <p className="text-sm text-slate-500 mt-1">
                  {(selectedFile?.size / 1024).toFixed(2)} KB
                </p>
              </div>

              {!analyzing && (
                <div className="space-y-4">
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <h4 className="font-semibold text-cyan-900 mb-2">What happens next:</h4>
                    <ul className="text-sm text-cyan-800 space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <span>AI analyzes reserve clauses and timelines</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <span>Reviews processing track record requirements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <span>Determines if dispute or legal claim is warranted</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                        <span>Provides actionable recommendations</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={handleUploadAndAnalyze}
                    className="w-full primary-button py-4 text-lg"
                    disabled={uploading}
                  >
                    Start AI Analysis
                  </button>
                </div>
              )}

              {analyzing && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Analyzing Contract...</h3>
                  <div className="space-y-3 mt-6">
                    <div className="flex items-center justify-center gap-3 text-slate-600">
                      <Clock size={20} className="animate-pulse" />
                      <span>Extracting reserve clauses...</span>
                    </div>
                    <div className="flex items-center justify-center gap-3 text-slate-600">
                      <FileText size={20} className="animate-pulse" />
                      <span>Reviewing timelines and terms...</span>
                    </div>
                    <div className="flex items-center justify-center gap-3 text-slate-600">
                      <TrendingUp size={20} className="animate-pulse" />
                      <span>Assessing recovery likelihood...</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mt-6">This usually takes 30-60 seconds</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Analysis Results */}
          {step === 3 && analysisResult && (
            <div className="space-y-6">
              {/* Recommendation Card */}
              <div className="bg-gradient-to-br from-purple-50 to-cyan-50 border border-purple-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Recommended Action</h3>
                    <p className="text-2xl font-bold text-purple-600 mb-3">
                      {analysisResult.recommended_action?.action_type || 'Analysis Complete'}
                    </p>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm text-slate-600">Recovery Likelihood:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getRecoveryLikelihoodColor(analysisResult.recommended_action?.recovery_likelihood)}`}>
                        {analysisResult.recommended_action?.recovery_likelihood || 'N/A'}
                      </span>
                      <span className="text-sm text-slate-600 ml-2">Estimated: {analysisResult.recommended_action?.estimated_recovery_percentage || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal Grounds */}
              {analysisResult.recommended_action?.legal_grounds && (
                <div className="border border-slate-200 rounded-lg p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Legal Grounds for Action</h4>
                  <ul className="space-y-2">
                    {analysisResult.recommended_action.legal_grounds.map((ground, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-1" />
                        <span className="text-slate-700">{ground}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Next Steps */}
              {analysisResult.recommended_action?.next_steps && (
                <div className="border border-slate-200 rounded-lg p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Next Steps</h4>
                  <ol className="list-decimal pl-5 space-y-2">
                    {analysisResult.recommended_action.next_steps.map((step, idx) => (
                      <li key={idx} className="text-slate-700">{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Key Findings */}
              {analysisResult.key_findings && (
                <div className="border border-slate-200 rounded-lg p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Key Findings</h4>
                  <ul className="space-y-2">
                    {analysisResult.key_findings.map((finding, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0 mt-2" />
                        <span className="text-slate-700">{finding}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4 border-t border-slate-200">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                >
                  Review Later
                </button>
                <button
                  onClick={handleFinalSubmit}
                  className="flex-1 primary-button py-3"
                >
                  Submit to Admin for Final Review
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadModal;
